import React, { FC } from "react";
import { IconType } from "react-icons";
import {
  LuAlertTriangle,
  LuArrowUpRight,
  LuBriefcase,
  LuBuilding2,
  LuClock,
  LuCreditCard,
  LuMail,
  LuReceipt,
  LuRepeat,
  LuTimer,
  LuUser,
} from "react-icons/lu";

import { actionCardText, ActionProposalKind } from "./actionProposal.utils";
import { AskProposal, proposalRows, proposalWarnings } from "./proposal.utils";

const ACTION_ICONS: Record<ActionProposalKind, IconType> = {
  start_sequence: LuMail,
  repeat_work: LuRepeat,
  create_invoice: LuReceipt,
  record_payment: LuCreditCard,
  add_time: LuClock,
  start_timer: LuTimer,
};

const CREATE_TEXT = {
  heading: "Ready to create",
  button: "Review & create",
  footer: "Nothing has been created yet.",
};

/*
 * "Ready to create" card in the Ask ProCharted panel. Review & create opens
 * the app's own New … window pre-filled; nothing is created from here.
 */

type Props = {
  proposal: AskProposal;
  onReview: () => void;
  onCancel: () => void;
  onOpenLink: (link: string) => void;
};

export const ProposalCard: FC<Props> = ({
  proposal,
  onReview,
  onCancel,
  onOpenLink,
}) => {
  const rows = proposalRows(proposal);
  const warnings = proposalWarnings(proposal);
  const duplicates =
    proposal.kind === "create_client" ? proposal.possibleDuplicates : [];
  const Icon =
    proposal.kind === "create_work"
      ? LuBriefcase
      : proposal.kind !== "create_client"
      ? ACTION_ICONS[proposal.kind]
      : proposal.fields.type === "organization"
      ? LuBuilding2
      : LuUser;
  const words =
    proposal.kind === "create_work" || proposal.kind === "create_client"
      ? CREATE_TEXT
      : actionCardText(proposal.kind);

  return (
    <section
      aria-label={words.heading}
      data-proposal-card={""}
      className={"rounded-[14px] border border-[#DCD3FF] bg-[#FBFAFF] p-3.5"}>
      <div className={"flex items-start gap-2.5"}>
        <span
          aria-hidden={"true"}
          className={
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-[#EEEAFF] text-[#6B4EFF]"
          }>
          <Icon size={16} strokeWidth={2.2} />
        </span>
        <div className={"min-w-0 flex-1"}>
          <div
            className={
              "text-[11px] font-extrabold uppercase tracking-[0.06em] text-[#6B4EFF]"
            }>
            {words.heading}
          </div>
          <div
            className={
              "text-[14px] font-bold leading-[1.4] text-[#1F2433] [overflow-wrap:anywhere]"
            }>
            {proposal.summary}
          </div>
        </div>
      </div>

      {rows.length > 0 && (
        <dl
          className={
            "mt-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 rounded-[10px] border border-[#EDEFF3] bg-white px-3 py-2.5 text-[13px] leading-[1.45]"
          }>
          {rows.map((row) => (
            <React.Fragment key={row.label}>
              <dt className={"font-semibold text-[#7A8194]"}>{row.label}</dt>
              <dd
                className={
                  "whitespace-pre-line font-semibold text-[#1F2433] [overflow-wrap:anywhere]"
                }>
                {row.value}
              </dd>
            </React.Fragment>
          ))}
        </dl>
      )}

      {(warnings.length > 0 || duplicates.length > 0) && (
        <div
          role={"note"}
          className={
            "mt-3 space-y-1.5 rounded-[10px] border border-[#F5DDA8] bg-[#FFF8E8] px-3 py-2.5 text-[12.5px] font-medium leading-[1.5] text-[#8A5A00]"
          }>
          {duplicates.length > 0 && (
            <div className={"flex items-start gap-2"}>
              <LuAlertTriangle
                aria-hidden={"true"}
                size={15}
                className={"mt-[2px] shrink-0 text-[#D18A00]"}
              />
              {/* One line: the duplicate isn't repeated in the warnings. */}
              <div className={"min-w-0 [overflow-wrap:anywhere]"}>
                <span className={"font-bold"}>
                  {duplicates.length === 1
                    ? "This client may already exist:"
                    : "These clients may already exist:"}
                </span>{" "}
                {duplicates.map((duplicate, index) => (
                  <React.Fragment key={`${duplicate.id}-${index}`}>
                    {index > 0 && ", "}
                    {duplicate.link ? (
                      <button
                        type={"button"}
                        title={duplicate.detail}
                        onClick={() =>
                          duplicate.link && onOpenLink(duplicate.link)
                        }
                        className={
                          "inline-flex items-center gap-0.5 font-bold text-[#6B4EFF] underline-offset-2 hover:underline"
                        }>
                        {duplicate.name}
                        <LuArrowUpRight size={13} strokeWidth={2.4} />
                      </button>
                    ) : (
                      <span className={"font-bold"} title={duplicate.detail}>
                        {duplicate.name}
                      </span>
                    )}
                    {duplicate.detail && (
                      <span className={"text-[#9A7A3A]"}>
                        {" "}
                        ({duplicate.detail})
                      </span>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          {warnings.map((warning) => (
            <div key={warning} className={"flex items-start gap-2"}>
              <LuAlertTriangle
                aria-hidden={"true"}
                size={15}
                className={"mt-[2px] shrink-0 text-[#D18A00]"}
              />
              <span className={"[overflow-wrap:anywhere]"}>{warning}</span>
            </div>
          ))}
        </div>
      )}

      <div className={"mt-3 flex flex-wrap items-center justify-between gap-2"}>
        <span className={"text-[12.5px] font-semibold text-[#7A8194]"}>
          {words.footer}
        </span>
        <div className={"flex gap-2"}>
          <button
            type={"button"}
            onClick={onCancel}
            className={
              "inline-flex h-9 items-center rounded-[10px] border border-[#DCE2EA] bg-white px-3.5 text-[13px] font-bold text-[#3A4256] hover:border-[#6B4EFF] hover:text-[#6B4EFF]"
            }>
            Cancel
          </button>
          <button
            type={"button"}
            onClick={onReview}
            className={
              "inline-flex h-9 items-center rounded-[10px] bg-[#6B4EFF] px-3.5 text-[13px] font-bold text-white hover:bg-[#5A3EE6]"
            }>
            {words.button}
          </button>
        </div>
      </div>
    </section>
  );
};
